import { useState } from "react";
import { SlidersHorizontal, ChevronDown } from "lucide-react";
import { cn, plural } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import DateRangeFilter from "@/components/DateRangeFilter";
import SearchInput from "@/components/SearchInput";
import MultiSelect from "@/components/MultiSelect";
import FilterPills from "@/components/FilterPills";
import { STATUSES, ATTENDANCE } from "@/hooks/useRegisterFilters";

/**
 * The register's filter block, rendered wherever the register's question is asked.
 *
 * One component, two screens: the register itself and Reports. The alternative was a
 * second copy on the Reports page, and this project has already watched two copies of a
 * filter set drift — the PDF export honoured `unit_id` while the Excel silently ignored
 * it, so one screen produced two different files.
 *
 * `count` and `noun` describe whatever the caller is counting, because Reports counts
 * the same rows for a different purpose.
 *
 * On a narrow screen the controls start collapsed. Twelve of them stacked is a long scroll
 * past filters somebody may not have come for — on Reports they are between HR and the
 * download button they opened the page to press. Desktop is untouched: there is room, and
 * the register is a screen you come to in order to filter.
 *
 * What is NOT collapsible is the pill row. A filter that is set must never be invisible:
 * a quietly narrowed register is one that gets exported and handed to somebody as though
 * it were the whole thing, which is the same failure the "About this export" sheet exists
 * to prevent. So the count, the pills and Clear all sit outside the fold, and the toggle
 * itself says how many are applied.
 */
export default function RegisterFilters({ state, count, noun = "training", footer = null }) {
  const { f, write, pills, clearAll, qInput, setQInput, setUnitsFor, options } = state;
  const [open, setOpen] = useState(false);

  return (
    <Card className="p-4 border border-border" data-testid="register-filters">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open}
              data-testid="filters-toggle"
              className="lg:hidden mb-4 w-full flex items-center justify-between gap-3 rounded-md
                         border border-border px-3 py-2 text-sm focus:outline-none
                         focus-visible:ring-2 focus-visible:ring-ring">
        <span className="flex items-center gap-2 font-medium">
          <SlidersHorizontal className="w-4 h-4 text-muted-foreground" />
          Filters
          {pills.length > 0 && (
            <span className="text-muted-foreground font-normal">
              · {plural(pills.length, "applied", "applied")}
            </span>
          )}
        </span>
        <ChevronDown className={cn("w-4 h-4 shrink-0 transition-transform", open && "rotate-180")} />
      </button>

      <div className={cn("space-y-4", !open && "hidden lg:block")} data-testid="filter-controls">
      <div>
        <div className="overline text-muted-foreground mb-2">The training</div>
        {/* Ten controls on five columns: two even rows, nothing dangling on a third. */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
          <SearchInput className="lg:col-span-2" value={qInput} onChange={setQInput}
                       placeholder="Search title, trainer or venue..." testid="trainings-search" />
          <DateRangeFilter value={{ from: f.date_from, to: f.date_to }}
                           onChange={({ from, to }) => write({ date_from: from, date_to: to })}
                           testid="filter-dates" />
          <Select value={f.status} onValueChange={(v) => write({ status: v })}>
            <SelectTrigger data-testid="filter-status" aria-label="Filter by status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={f.type} onValueChange={(v) => write({ type: v })}>
            <SelectTrigger data-testid="filter-type" aria-label="Filter by training type"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All types</SelectItem>
              <SelectItem value="INTERNAL">Internal</SelectItem>
              <SelectItem value="EXTERNAL">External</SelectItem>
            </SelectContent>
          </Select>
          <MultiSelect value={f.units} onChange={setUnitsFor("units", "scopeDepts")} options={options.units}
                       placeholder="All units" noun="unit" searchPlaceholder="Search units..."
                       empty="No units on record yet." testid="filter-unit" />
          <MultiSelect value={f.scopeDepts} onChange={(v) => write({ scopeDepts: v })} options={options.scopeDepts}
                       placeholder={f.units.length ? "Any of their departments" : "For any department"}
                       noun="department" searchPlaceholder="Search departments..."
                       empty={f.units.length ? "The selected units have no departments on record." : "No departments on record yet."}
                       testid="filter-scope-department" />
          <MultiSelect value={f.categories} onChange={(v) => write({ categories: v })} options={options.categories}
                       placeholder="Any category" noun="category" searchPlaceholder="Search categories..."
                       empty="No categories set up yet." testid="filter-category" />
          <MultiSelect value={f.trainers} onChange={(v) => write({ trainers: v })} options={options.trainers}
                       placeholder="Any trainer" noun="trainer" searchPlaceholder="Search trainers..."
                       empty="No trainers on record yet." testid="filter-trainer" />
          {/* The audit question: which delivered sessions have no signed sheet behind them. */}
          <Select value={f.sheet} onValueChange={(v) => write({ sheet: v })}>
            <SelectTrigger data-testid="filter-sheet" aria-label="Filter by signed attendance sheet"><SelectValue placeholder="Signed sheet" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Signed sheet: any</SelectItem>
              <SelectItem value="missing">Signed sheet missing</SelectItem>
              <SelectItem value="on_file">Signed sheet on file</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <div className="overline text-muted-foreground mb-2">Attended by</div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          <MultiSelect value={f.employees} onChange={(v) => write({ employees: v })} options={options.emps}
                       placeholder="Anyone" noun="employee" searchPlaceholder="Search name or code..."
                       empty="No employees on record yet." testid="filter-employee" />
          <MultiSelect value={f.attendeeUnits} onChange={setUnitsFor("attendeeUnits", "depts")} options={options.units}
                       placeholder="From any unit" noun="unit" searchPlaceholder="Search units..."
                       empty="No units on record yet." testid="filter-attendee-unit" />
          <MultiSelect value={f.depts} onChange={(v) => write({ depts: v })} options={options.attendeeDepts}
                       placeholder="From any department" noun="department" searchPlaceholder="Search departments..."
                       empty={f.attendeeUnits.length ? "The selected units have no departments on record." : "No departments on record yet."}
                       testid="filter-department" />
          <MultiSelect value={f.attendance} onChange={(v) => write({ attendance: v })}
                       options={ATTENDANCE.map((a) => ({ id: a, name: a }))}
                       placeholder="Any attendance" noun="status" searchPlaceholder="Search..."
                       testid="filter-attendance" />
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          These describe one person: "Production" and "Present" together find trainings somebody
          from Production actually attended, not ones where they were listed and somebody else turned up.
        </p>
      </div>

      </div>

      {pills.length > 0 && (
        <div className="mt-4">
          <FilterPills pills={pills} count={count} noun={noun} onClearAll={clearAll} />
        </div>
      )}
      {footer && <div className="mt-3">{footer}</div>}
    </Card>
  );
}
